import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, ChevronDown, ChevronRight, Loader2, Lock, Save, Undo2 } from 'lucide-react';
import { useMatriz, useSalvarMatriz } from '../hooks';
import type { FeaturePermission, PermissionPatch, Role } from '../api';

const ACOES = [
  { key: 'canView', label: 'Visualizar' },
  { key: 'canCreate', label: 'Criar' },
  { key: 'canEdit', label: 'Editar' },
  { key: 'canDelete', label: 'Deletar' },
] as const;

type AcaoKey = (typeof ACOES)[number]['key'];

interface Props {
  perfil: Role;
  onSaved: (message: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}

export const MatrizPermissoes: React.FC<Props> = ({ perfil, onSaved, onDirtyChange }) => {
  const { data, isLoading, isError } = useMatriz(perfil.id);
  const salvar = useSalvarMatriz();

  const [draft, setDraft] = useState<FeaturePermission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [recolhidos, setRecolhidos] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDraft(data?.features ?? []);
    setError(null);
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return draft.some((row, index) => {
      const original = data.features[index];
      return (
        original &&
        ACOES.some(({ key }) => row[key] !== original[key])
      );
    });
  }, [draft, data]);

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const porModulo = useMemo(() => {
    const groups = new Map<string, FeaturePermission[]>();
    for (const row of draft) {
      const list = groups.get(row.module) ?? [];
      list.push(row);
      groups.set(row.module, list);
    }
    return [...groups.entries()];
  }, [draft]);

  function toggle(featureId: string, acao: AcaoKey) {
    setDraft((prev) =>
      prev.map((row) => (row.featureId === featureId ? { ...row, [acao]: !row[acao] } : row)),
    );
  }

  /** Marca ou desmarca a ação em todas as linhas visíveis, conforme o estado atual. */
  function toggleColuna(acao: AcaoKey) {
    const todosMarcados = draft.every((row) => row[acao]);
    setDraft((prev) => prev.map((row) => ({ ...row, [acao]: !todosMarcados })));
  }

  function alternarGrupo(modulo: string) {
    setRecolhidos((prev) => {
      const next = new Set(prev);
      if (next.has(modulo)) next.delete(modulo);
      else next.add(modulo);
      return next;
    });
  }

  const todosRecolhidos = porModulo.length > 0 && porModulo.every(([modulo]) => recolhidos.has(modulo));

  function alternarTodosGrupos() {
    setRecolhidos(todosRecolhidos ? new Set() : new Set(porModulo.map(([modulo]) => modulo)));
  }

  function reverter() {
    setDraft(data?.features ?? []);
    setError(null);
  }

  async function handleSave() {
    setError(null);
    const permissions: PermissionPatch[] = draft.map((row) => ({
      featureId: row.featureId,
      canView: row.canView,
      canCreate: row.canCreate,
      canEdit: row.canEdit,
      canDelete: row.canDelete,
    }));
    try {
      const updated = await salvar.mutateAsync({ roleId: perfil.id, permissions });
      setDraft(updated.features);
      onSaved(`Permissões de "${perfil.name}" salvas.`);
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string | string[] } } }).response;
      const message = response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message ?? 'Não foi possível salvar as permissões.');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Carregando matriz…
      </div>
    );
  }
  if (isError) {
    return <div className="py-16 text-center text-red-600">Não foi possível carregar a matriz.</div>;
  }

  const totalConcedido = draft.filter((r) => ACOES.some(({ key }) => r[key])).length;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{perfil.name}</h2>
          <p className="text-sm text-gray-500">
            <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{perfil.slug}</code>
            {' · '}
            {totalConcedido} de {draft.length} funcionalidades com acesso
            {perfil.isSystem && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                <Lock className="h-3 w-3" /> perfil de sistema
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
              Alterações não salvas
            </span>
          )}
          <button
            type="button"
            onClick={reverter}
            disabled={!dirty || salvar.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Undo2 className="h-4 w-4" /> Reverter
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || salvar.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {salvar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {perfil.isSystem && (
        <div className="mx-5 mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          O Administrador ignora a matriz e acessa tudo — o bypass existe para que um erro de
          configuração não tranque ninguém fora desta tela. As marcações abaixo valem como registro.
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-5 pb-4">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              <th className="border-b border-gray-200 px-3 py-2 text-left">
                <span className="block font-semibold text-gray-700">Funcionalidade</span>
                <button
                  type="button"
                  onClick={alternarTodosGrupos}
                  className="mt-0.5 text-xs font-normal text-blue-600 hover:underline"
                >
                  {todosRecolhidos ? 'expandir todos' : 'recolher todos'}
                </button>
              </th>
              {ACOES.map(({ key, label }) => (
                <th key={key} className="w-28 border-b border-gray-200 px-3 py-2 text-center">
                  <span className="block font-semibold text-gray-700">{label}</span>
                  <button
                    type="button"
                    onClick={() => toggleColuna(key)}
                    className="mt-0.5 text-xs font-normal text-blue-600 hover:underline"
                  >
                    marcar todos
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {porModulo.map(([modulo, rows]) => {
              const recolhido = recolhidos.has(modulo);
              const concedidos = rows.filter((r) => ACOES.some(({ key }) => r[key])).length;
              return (
              <React.Fragment key={modulo}>
                <tr>
                  <td colSpan={5} className="border-b border-gray-200 bg-gray-50 p-0">
                    <button
                      type="button"
                      onClick={() => alternarGrupo(modulo)}
                      aria-expanded={!recolhido}
                      className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 hover:bg-gray-100"
                    >
                      {recolhido ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {modulo}
                      <span className="ml-auto font-normal normal-case tracking-normal text-gray-400">
                        {concedidos}/{rows.length} com acesso
                      </span>
                    </button>
                  </td>
                </tr>
                {!recolhido && rows.map((row) => (
                  <tr key={row.featureId} className="hover:bg-blue-50/40">
                    <td className="border-b border-gray-100 px-3 py-2">
                      <span className="text-gray-900">{row.label}</span>
                      <code className="ml-2 text-xs text-gray-400">{row.key}</code>
                    </td>
                    {ACOES.map(({ key, label }) => (
                      <td key={key} className="border-b border-gray-100 px-3 py-2 text-center">
                        <label className="inline-flex cursor-pointer items-center justify-center">
                          <input
                            type="checkbox"
                            checked={row[key]}
                            onChange={() => toggle(row.featureId, key)}
                            aria-label={`${label} — ${row.label}`}
                            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {draft.length === 0 && (
          <div className="py-12 text-center text-gray-500">
            <Check className="mx-auto mb-2 h-6 w-6" />
            Nenhuma funcionalidade ativa no catálogo.
          </div>
        )}
      </div>
    </div>
  );
};
